import React, { createContext, useState, useContext, useEffect } from 'react';

// Define theme types
export type ThemeType = 'device' | 'light' | 'dark';

// Context interface
interface ThemeContextType {
  theme: ThemeType;
  setTheme: (theme: ThemeType) => void;
  isDarkMode: boolean;
}

// Create context with default values
const ThemeContext = createContext<ThemeContextType>({
  theme: 'device',
  setTheme: () => {},
  isDarkMode: false,
});

// Custom hook to use theme context
export const useTheme = () => useContext(ThemeContext);

// Theme provider component
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Get saved theme from localStorage or use device default
  const getSavedTheme = (): ThemeType => {
    const savedTheme = localStorage.getItem('theme');
    return (savedTheme as ThemeType) || 'device';
  };

  const [theme, setThemeState] = useState<ThemeType>(getSavedTheme());
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);

  // Apply the theme class to the document
  const applyTheme = (newTheme: ThemeType) => {
    const root = window.document.documentElement;
    
    // First, remove any existing theme classes
    root.classList.remove('light', 'dark');
    
    // Determine if we should use dark mode
    let shouldUseDarkMode = false;
    
    if (newTheme === 'dark') {
      shouldUseDarkMode = true;
    } else if (newTheme === 'device') {
      // Check the device/system preference for dark mode
      shouldUseDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    
    // Apply the appropriate class based on the determination
    if (shouldUseDarkMode) {
      root.classList.add('dark');
    } else {
      root.classList.add('light');
    }
    
    // Update the dark mode state
    setIsDarkMode(shouldUseDarkMode);
  };

  // Set the theme and save it to localStorage
  const setTheme = (newTheme: ThemeType) => {
    localStorage.setItem('theme', newTheme);
    setThemeState(newTheme);
    applyTheme(newTheme);
  };

  // Listen for changes in the device/system theme
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    // When the device theme changes, apply it if we're using device theme
    const handleChange = () => {
      if (theme === 'device') {
        applyTheme('device');
      }
    };
    
    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    }
    
    // Apply the theme initially
    applyTheme(theme);
    
    // Cleanup
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      }
    };
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider; 